/**
 * Single source of truth for the entretien-tech FAQ.
 *
 * Used by:
 *  - layout.tsx → JSON-LD FAQPage schema (invisible, Google + LLM)
 *  - page.tsx → visible <FaqSection> at the bottom (humans, schema-content match)
 *
 * Keeping FR + EN here means schema and visible content can never drift apart.
 */

import type { FaqItem } from "@/components/landing/FaqSection";

export const techFaqFr: FaqItem[] = [
  {
    q: "Comment se préparer à un entretien technique FAANG ?",
    a: "Les entretiens FAANG (Google, Meta, Amazon, Apple, Netflix) combinent coding (algorithmes, structures de données), system design et questions comportementales. Velyq simule les 3 types avec des questions niveau L4/L5, correction ligne par ligne pour le code, et feedback sur ton System Design en temps réel.",
  },
  {
    q: "Velyq remplace-t-il LeetCode ?",
    a: "Non, c'est complémentaire. LeetCode te fait résoudre des problèmes en autonomie. Velyq simule l'entretien réel : tu expliques ton raisonnement à voix haute, l'IA te relance comme un vrai recruteur, et tu reçois un feedback sur ta communication technique — pas juste sur le code.",
  },
  {
    q: "Quels langages de programmation sont supportés ?",
    a: "Python, JavaScript, TypeScript, Java, Go, C++, Rust, Kotlin, Swift. Tu peux choisir ton langage avant chaque session et l'IA adapte ses questions et sa grille de correction aux idiomes du langage.",
  },
  {
    q: "Est-ce utile pour un entretien system design ?",
    a: "Oui. Velyq génère des cas classiques (design Twitter, Uber, TinyURL, rate limiter, feed ranking) et des cas plus spécifiques (data pipeline, stream processing, ML serving). L'IA challenge tes choix de base de données, de caching, de partitioning comme un staff engineer le ferait.",
  },
  {
    q: "Velyq couvre-t-il les entretiens data / ML / DevOps ?",
    a: "Oui. Mode spécialisé data scientist (SQL, stats, ML theory, cas produit), data engineer (pipelines, Airflow, Spark), DevOps/SRE (incident response, observabilité, IaC) et ML engineer (model deployment, MLOps, serving).",
  },
  {
    q: "Je peux m'entraîner en anglais pour des boîtes US ?",
    a: "Oui. Bascule l'interface et les entretiens en anglais depuis /settings. L'IA te corrige aussi sur l'anglais technique (vocabulary, clarity) pour que tu sois prêt à postuler chez Google, Meta, Stripe, Datadog, etc.",
  },
  {
    q: "Combien de temps pour se préparer sérieusement ?",
    a: "Pour un entretien FAANG, compte 4 à 8 semaines avec 3-4 sessions Velyq par semaine + du LeetCode en parallèle. Pour une scale-up française ou un poste junior, 2-3 semaines suffisent. Velyq te propose un plan de préparation personnalisé depuis le dashboard.",
  },
];

export const techFaqEn: FaqItem[] = [
  {
    q: "How do I prepare for a FAANG technical interview?",
    a: "FAANG interviews (Google, Meta, Amazon, Apple, Netflix) combine coding (algorithms, data structures), system design and behavioral questions. Velyq simulates the 3 types with L4/L5 level questions, line-by-line code review, and real-time System Design feedback.",
  },
  {
    q: "Does Velyq replace LeetCode?",
    a: "No, it's complementary. LeetCode has you solve problems on your own. Velyq simulates the real interview: you explain your reasoning out loud, the AI probes you like a real interviewer, and you get feedback on your technical communication — not just your code.",
  },
  {
    q: "Which programming languages are supported?",
    a: "Python, JavaScript, TypeScript, Java, Go, C++, Rust, Kotlin, Swift. You can pick your language before each session and the AI adapts its questions and grading to the language's idioms.",
  },
  {
    q: "Is it useful for system design interviews?",
    a: "Yes. Velyq generates classic cases (design Twitter, Uber, TinyURL, rate limiter, feed ranking) and more specific cases (data pipeline, stream processing, ML serving). The AI challenges your DB, caching and partitioning choices like a staff engineer would.",
  },
  {
    q: "Does Velyq cover data / ML / DevOps interviews?",
    a: "Yes. Specialized modes: data scientist (SQL, stats, ML theory, product cases), data engineer (pipelines, Airflow, Spark), DevOps/SRE (incident response, observability, IaC) and ML engineer (model deployment, MLOps, serving).",
  },
  {
    q: "Can I practice in English for US companies?",
    a: "Yes. Switch the interface and interviews to English from /settings. The AI also corrects your technical English (vocabulary, clarity) so you're ready to apply at Google, Meta, Stripe, Datadog, etc.",
  },
  {
    q: "How long should serious prep take?",
    a: "For a FAANG interview, plan 4 to 8 weeks with 3-4 Velyq sessions per week + LeetCode in parallel. For a European scale-up or junior position, 2-3 weeks is enough. Velyq offers a personalized prep plan from the dashboard.",
  },
];
